import { CT_STATUS } from '@exodus/assets';
import useFuseSearch from '@exodus/react-hooks/common/useFuseSearch';
import { useDebounceCallback } from '@passkeys/hooks';
import { useCallback, useRef, useState } from 'react';

import type { Asset, Assets, CustomToken } from '@/types';
import exodus from '@/ui/exodus';
import useBaseAssets from '@/ui/hooks/use-base-assets';
import useAssetsRef from './use-assets-ref.js';
import { toAsset } from '../utils/assets.js';
import { sortFuseResult } from '../utils/sorting.js';

const useTokenSearch = () => {
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState<string>();
  const [unsortedValue, setUnsortedValue] = useState<Asset[]>([]);
  const [error, setError] = useState<unknown>();

  const baseAssets = useBaseAssets();

  const assetsRef = useRef<Assets>({});
  useAssetsRef(assetsRef);

  const searchContract = useCallback(
    async (address: string, chains: string[]) => {
      const promises = chains.map(async (assetName) => {
        const asset = assetsRef.current[assetName];
        if (!asset.address.validate(address)) {
          return;
        }

        const token: CustomToken = await exodus.customTokens.fetchToken(
          address,
          asset,
        );
        if (!token) {
          return;
        }

        if (token.lifecycleStatus === CT_STATUS.DISABLED) {
          throw new Error(
            `Unable to add token with address '${address}' as it might be malicious.`,
          );
        }

        return toAsset({ assets: assetsRef.current, token });
      });
      const results = await Promise.allSettled(promises);

      return results
        .filter(
          (result): result is PromiseFulfilledResult<Asset> =>
            result.status === 'fulfilled' && !!result.value,
        )
        .map((result) => result.value);
    },
    [],
  );

  const searchToken = useCallback(
    async (searchTerm: string, chains: string[]) => {
      const tokens = await exodus.customTokens.searchTokens({
        lifecycleStatus: [CT_STATUS.CURATED, CT_STATUS.VERIFIED],
        baseAssetName: chains,
        query: searchTerm,
      });

      const tokenAssets = tokens.map((token) =>
        toAsset({ assets: assetsRef.current, token }),
      );

      const filteredBaseAssets = baseAssets.filter(
        (asset) => !chains?.length || chains.includes(asset.name),
      );

      return [...filteredBaseAssets, ...tokenAssets];
    },
    [baseAssets],
  );

  const checkIsAddress = useCallback(
    async (searchTerm: string, chains: string[]) => {
      const promises = chains.map((assetName) =>
        assetsRef.current[assetName].api.validateAssetId?.(searchTerm),
      );
      const results = await Promise.allSettled(promises);

      return results.some(
        (result) => result.status === 'fulfilled' && !!result.value,
      );
    },
    [],
  );

  const search = useCallback(
    async (searchTerm: string, chains: string[]) => {
      let result = [] as Asset[];

      if (searchTerm.length < 3) {
        setUnsortedValue(result);
        return;
      }

      try {
        setLoading(true);

        const isAddress = await checkIsAddress(searchTerm, chains);

        result = isAddress
          ? await searchContract(searchTerm, chains)
          : await searchToken(searchTerm, chains);
      } catch (error) {
        setError(error);
      }

      setLoading(false);
      setQuery(searchTerm);
      setUnsortedValue(result);
    },
    [checkIsAddress, searchContract, searchToken],
  );

  const value: Asset[] = useFuseSearch(unsortedValue, query, {
    keys: ['displayTicker', 'displayName', 'assetId', 'displayNetworkName'],
    threshold: 0.1,
    shouldSort: true,
    sortFn: sortFuseResult,
  });

  const debouncedSearch = useDebounceCallback(search, 300) as typeof search;

  return {
    search: debouncedSearch,
    loading,
    value,
    error,
  };
};

export default useTokenSearch;
